import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth, getUserData } from '../../utils/auth';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

interface Consultation {
  id: string;
  date: string;
  consultant: string;
  consultantId?: string;
  consultantImage?: string | null;
  durationSeconds: number;
  paid: number;
  status: 'scheduled' | 'completed' | 'cancelled';
  type: string;
  notes?: string;
}

interface ConversationMessage {
  role: 'assistant' | 'user' | 'system';
  content: string;
  createdAt: string;
}

interface UserConversationApiItem {
  id: string;
  consultantName: string | null;
  consultantId?: string;
  status: 'active' | 'completed' | 'cancelled' | string;
  duration: number;
  startDate: string | null;
  endDate: string | null;
  paid: number;
  createdAt: string;
  updatedAt: string;
}

interface UserConversationApiResponse {
  conversations?: UserConversationApiItem[];
}

const Consultas: React.FC = () => {
  const navigate = useNavigate();
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [selectedConsultation, setSelectedConsultation] = useState<Consultation | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const mapConversation = (conv: UserConversationApiItem): Consultation => {
      const normalizedStatus: Consultation['status'] =
        conv.status === 'completed' ? 'completed' : conv.status === 'cancelled' ? 'cancelled' : 'scheduled';

      return {
        id: conv.id,
        date: conv.startDate || conv.createdAt,
        consultant: conv.consultantName || '—',
        consultantId: conv.consultantId,
        durationSeconds: Number.isFinite(conv.duration) ? conv.duration : 0,
        paid: Number.isFinite(conv.paid) ? conv.paid : 0,
        status: normalizedStatus,
        type: 'Conversa',
      };
    };

    const loadConsultations = async () => {
      setLoading(true);
      try {
        const user = getUserData();
        if (!user?.id) {
          if (!cancelled) setConsultations([]);
          return;
        }

        const response = await fetchWithAuth(`/api/conversations/getAllUsersConversations`, {method: 'POST'});
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data: UserConversationApiResponse = await response.json();
        const rows = (data.conversations || []).map(mapConversation);
        if (!cancelled) setConsultations(rows);
      } catch (error) {
        console.error('Erro ao carregar consultas:', error);
        if (!cancelled) setConsultations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadConsultations();

    return () => {
      cancelled = true;
    };
  }, []);

  const formatDuration = (seconds: number): string => {
    const safeSeconds = Math.max(0, Math.floor(seconds));
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const openConsultationModal = async (consultation: Consultation) => {
    setSelectedConsultation(consultation);
    setMessagesLoading(true);
    setMessages([]);

    try {
      // Fetch messages and consultant image in parallel
      const [msgResponse, infoResponse] = await Promise.all([
        fetchWithAuth(`/api/conversations/${consultation.id}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }),
        fetchWithAuth(`/api/conversations/info`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ conversationId: consultation.id }),
        }),
      ]);

      // Process consultant image
      if (infoResponse.ok) {
        const infoData = await infoResponse.json();
        const performerImage = infoData.performer?.image
          ? (infoData.performer.image.startsWith('http')
              ? infoData.performer.image
              : `/api${infoData.performer.image}`)
          : null;
        
        setSelectedConsultation(prev => prev ? { ...prev, consultantImage: performerImage } : prev);
      }

      // Process messages
      if (!msgResponse.ok) {
        throw new Error(`HTTP ${msgResponse.status}`);
      }

      const data = await msgResponse.json();
      const normalizedMessages = Array.isArray(data?.messages)
        ? data.messages.map((message: any) => ({
            role: message.role,
            content: message.content,
            createdAt: message.createdAt,
          }))
        : [];

      setMessages(normalizedMessages);
    } catch (error) {
      console.error('Erro ao carregar mensagens da consulta:', error);
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  const filteredConsultations = consultations; // no filters, show all

  const getStatusBadge = (status: Consultation['status']) => {
    const styles = {
      scheduled: 'bg-sky-500/10 text-sky-200 border border-sky-500/30',
      completed: 'bg-emerald-500/10 text-emerald-200 border border-emerald-500/30',
      cancelled: 'bg-rose-500/10 text-rose-200 border border-rose-500/30',
    };
    const labels = {
      scheduled: 'A decorrer',
      completed: 'Concluída',
      cancelled: 'Cancelada',
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  // Build consultant avatar for modal messages
  const ConsultantMessageAvatar = ({ consultation }: { consultation: Consultation }) => {
    if (consultation.consultantImage) {
      return (
        <img
          src={consultation.consultantImage}
          alt={consultation.consultant}
          className="w-6 h-6 sm:w-7 sm:h-7 rounded-full object-cover border border-[#7c3aed]/40 flex-shrink-0"
        />
      );
    }
    const initials = (consultation.consultant || 'C').substring(0, 2).toUpperCase();
    return (
      <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gradient-to-br from-[#e2b714] to-[#e86fde] flex items-center justify-center text-[0.6rem] sm:text-[0.65rem] font-semibold flex-shrink-0">
        {initials}
      </div>
    );
  };

  if (loading) {
    return <div className="flex justify-center items-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
    </div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">Minhas Consultas</h2>
          <p className="text-xs sm:text-sm text-[#94a3b8]">Acompanhe e reveja as suas conversas</p>
        </div>
        <button
          onClick={() => {
            localStorage.setItem('pendingScrollSection', 'consultores');
            navigate('/');
          }}
          className="self-start sm:self-auto px-4 py-2 rounded-full bg-gradient-to-r from-[#8b5cf6] to-[#ec4899] text-white text-sm font-medium hover:brightness-110 transition"
        >
          Nova Consulta
        </button>
      </div>

      <div className="rounded-xl border border-[#111827] bg-[#0b1220] shadow-[0_12px_40px_rgba(0,0,0,0.35)] overflow-x-auto w-full block">
        <Table>
          <TableCaption className="px-4 pb-4">Datas e horas em fuso PT.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">Data</TableHead>
              <TableHead className="whitespace-nowrap hidden sm:table-cell">Hora</TableHead>
              <TableHead className="whitespace-nowrap hidden md:table-cell">Tipo</TableHead>
              <TableHead className="whitespace-nowrap">Consultor</TableHead>
              <TableHead className="text-right whitespace-nowrap hidden sm:table-cell">Duração</TableHead>
              <TableHead className="whitespace-nowrap">Status</TableHead>
              <TableHead className="text-right whitespace-nowrap">Conversa</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredConsultations.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="text-[#cbd5e1] whitespace-nowrap text-xs sm:text-sm">
                  {new Date(c.date).toLocaleDateString('pt-PT')}
                </TableCell>
                <TableCell className="text-[#cbd5e1] whitespace-nowrap hidden sm:table-cell">
                  {new Date(c.date).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                </TableCell>
                <TableCell className="font-medium text-white whitespace-nowrap hidden md:table-cell">{c.type}</TableCell>
                <TableCell className="text-[#cbd5e1] whitespace-nowrap text-xs sm:text-sm">{c.consultant}</TableCell>
                <TableCell className="text-right text-white whitespace-nowrap hidden sm:table-cell">{formatDuration(c.durationSeconds)}</TableCell>
                <TableCell className="whitespace-nowrap">{getStatusBadge(c.status)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button
                    onClick={() => openConsultationModal(c)}
                    className="inline-flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[0.68rem] sm:text-xs font-medium bg-white/5 text-[#e5e7eb] border border-[#1f2937] hover:bg-white/10 transition"
                  >
                    <span className="hidden sm:inline">Ver detalhes</span>
                    <span className="sm:hidden">Ver</span>
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {selectedConsultation && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-0 sm:px-4 py-0 sm:py-4" role="dialog" aria-modal="true" aria-label="Detalhes da consulta">
          <div className="absolute inset-0 bg-black/70" onClick={() => setSelectedConsultation(null)} />
          <div className="relative z-10 w-full sm:max-w-3xl h-[95vh] sm:max-h-[90vh] flex flex-col rounded-t-2xl sm:rounded-2xl border border-[#1f2937] bg-[#0b1220] shadow-[0_24px_50px_rgba(0,0,0,0.45)]">
            <div className="flex items-center justify-between border-b border-[#1f2937] px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0">
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-white">Detalhes da Consulta</h3>
                <p className="text-xs sm:text-sm text-[#94a3b8] truncate">Sessão #{selectedConsultation.id.slice(0, 8)}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedConsultation(null)}
                className="rounded-md border border-[#334155] px-2.5 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm text-[#e2e8f0] hover:bg-white/5 flex-shrink-0 ml-3"
              >
                Fechar
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-3 border-b border-[#1f2937] px-3 sm:px-6 py-3 sm:py-4 text-sm text-[#cbd5e1] flex-shrink-0">
              <div className="rounded-lg border border-[#1f2937] bg-[#0f172a] p-2 sm:p-3">
                <p className="text-[0.6rem] sm:text-xs uppercase tracking-wide text-[#94a3b8]">Valor Pago</p>
                <p className="mt-0.5 sm:mt-1 text-sm sm:text-base font-semibold text-white">{selectedConsultation.paid.toFixed(2)} €</p>
              </div>
              <div className="rounded-lg border border-[#1f2937] bg-[#0f172a] p-2 sm:p-3">
                <p className="text-[0.6rem] sm:text-xs uppercase tracking-wide text-[#94a3b8]">Duração</p>
                <p className="mt-0.5 sm:mt-1 text-sm sm:text-base font-semibold text-white">{formatDuration(selectedConsultation.durationSeconds)}</p>
              </div>
              <div className="rounded-lg border border-[#1f2937] bg-[#0f172a] p-2 sm:p-3">
                <p className="text-[0.6rem] sm:text-xs uppercase tracking-wide text-[#94a3b8]">Consultor</p>
                <p className="mt-0.5 sm:mt-1 text-sm sm:text-base font-semibold text-white truncate">{selectedConsultation.consultant}</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2 sm:space-y-3 px-3 sm:px-6 py-3 sm:py-4">
              {messagesLoading && (
                <p className="text-sm text-[#94a3b8]">A carregar mensagens...</p>
              )}

              {!messagesLoading && messages.length === 0 && (
                <p className="text-sm text-[#94a3b8]">Não existem mensagens para esta consulta.</p>
              )}

              {!messagesLoading && messages.map((message, index) => {
                const isAssistant = message.role === 'assistant';
                return (
                  <div
                    key={`${message.createdAt}-${index}`}
                    className={`flex gap-2 sm:gap-3 ${isAssistant ? '' : 'flex-row-reverse'}`}
                  >
                    {/* Avatar */}
                    {isAssistant ? (
                      <ConsultantMessageAvatar consultation={selectedConsultation} />
                    ) : (
                      <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gradient-to-br from-[#3b82f6] to-[#8b5cf6] flex items-center justify-center flex-shrink-0">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </div>
                    )}

                    {/* Message bubble */}
                    <div
                      className={`flex-1 rounded-xl border p-2.5 sm:p-3 max-w-[85%] ${isAssistant ? 'border-[#7c3aed]/30 bg-[#581c87]/10' : 'border-[#1f2937] bg-[#111827]'}`}
                    >
                      <div className="mb-1 flex items-center justify-between text-[0.62rem] sm:text-xs text-[#94a3b8] gap-2">
                        <span>{isAssistant ? selectedConsultation.consultant : 'Você'}</span>
                        <span className="whitespace-nowrap">{new Date(message.createdAt).toLocaleString('pt-PT')}</span>
                      </div>
                      <p className="whitespace-pre-wrap text-xs sm:text-sm text-[#e5e7eb] break-words">{message.content}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Consultas;
